import { ImgWithFallback } from "@/components/img-with-fallback";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { brandDisplayColors } from "@shared/brand-display-colors";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users, Package, Megaphone, Tag, Target, ArrowRight, BookOpen,
  Layers, Shield, Building2, Swords, Compass, Heart, Flag,
  Volume2, CheckCircle, XCircle, AlertTriangle, TrendingUp,
  Crosshair, Zap, Eye, Sparkles, ChevronRight, Star, ImageIcon,
  Settings, Palette, Pencil, Trash2, Loader2, Wand2
} from "lucide-react";
import { BrandDesignSystemTab } from "@/components/brand-design-system-tab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "wouter";
import type { Brand, Persona, Product, AdUnitType, Offer, StrategyNode, Campaign, Narrative, NarrativeAngle, ProductReview } from "@shared/schema";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { optimizedImg } from "@/lib/img";
import { useMemo, useState } from "react";
import { ShareDialog } from "@/components/share-dialog";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AWARENESS_COLORS: Record<string, { dot: string }> = {
  unaware: { dot: "bg-red-400" },
  problem_aware: { dot: "bg-amber-400" },
  solution_aware: { dot: "bg-blue-400" },
  product_aware: { dot: "bg-violet-400" },
  most_aware: { dot: "bg-emerald-400" },
};

const FAMILY_COLORS: Record<string, string> = {
  conflict: "bg-red-500/10 text-red-500",
  identity: "bg-violet-500/10 text-violet-500",
  outcome: "bg-emerald-500/10 text-emerald-500",
  trust: "bg-blue-500/10 text-blue-500",
  "life-fit": "bg-amber-500/10 text-amber-500",
  worldview: "bg-teal-500/10 text-teal-500",
};

function NavStatCard({ title, value, icon: Icon, href, color }: {
  title: string; value: number; icon: any; href: string; color: string;
}) {
  return (
    <Link href={href}>
      <div className="group flex items-center gap-3 px-4 py-3.5 rounded-xl border border-border/50 hover:border-primary/20 hover:shadow-sm hover:shadow-primary/5 transition-all duration-200 cursor-pointer" data-testid={`stat-${title.toLowerCase()}`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold tracking-tight leading-none">{value}</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{title}</span>
          </div>
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/0 group-hover:text-primary transition-colors shrink-0" />
      </div>
    </Link>
  );
}

const DOCTRINE_ACCENTS: Record<string, { bg: string; ring: string; icon: string; dot: string }> = {
  primary:   { bg: "bg-primary/[0.04]",  ring: "ring-primary/15",         icon: "text-primary",         dot: "bg-primary" },
  amber:     { bg: "bg-amber-500/[0.05]", ring: "ring-amber-500/20",       icon: "text-amber-600",       dot: "bg-amber-500" },
  rose:      { bg: "bg-rose-500/[0.05]",  ring: "ring-rose-500/20",        icon: "text-rose-600",        dot: "bg-rose-500" },
  emerald:   { bg: "bg-emerald-500/[0.05]", ring: "ring-emerald-500/20",   icon: "text-emerald-600",     dot: "bg-emerald-500" },
  sky:       { bg: "bg-sky-500/[0.05]",   ring: "ring-sky-500/20",         icon: "text-sky-600",         dot: "bg-sky-500" },
};

function DoctrineBlock({
  icon: Icon,
  label,
  content,
  accent = "primary",
  variant = "card",
}: {
  icon: any;
  label: string;
  content: string | null | undefined;
  accent?: keyof typeof DOCTRINE_ACCENTS;
  variant?: "card" | "hero";
}) {
  if (!content) return null;
  const a = DOCTRINE_ACCENTS[accent] || DOCTRINE_ACCENTS.primary;
  const isHero = variant === "hero";
  return (
    <div
      className={`rounded-2xl ${a.bg} ring-1 ${a.ring} ${isHero ? "p-6" : "p-5"} h-full flex flex-col`}
      data-testid={`doctrine-block-${label.toLowerCase()}`}
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-7 h-7 rounded-lg bg-background/70 flex items-center justify-center shadow-sm ring-1 ${a.ring}`}>
          <Icon className={`h-3.5 w-3.5 ${a.icon}`} />
        </div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground/60">{label}</p>
      </div>
      <p className={`${isHero ? "text-[14px]" : "text-[13px]"} text-foreground/85 leading-relaxed whitespace-pre-line`}>
        {content}
      </p>
    </div>
  );
}

const ACCENT_STYLES: Record<string, string> = {
  primary: "bg-primary/10 text-primary",
  destructive: "bg-destructive/10 text-destructive",
};

function NumberedItem({ index, text, accent = "primary" }: { index: number; text: string; accent?: string }) {
  return (
    <li className="flex gap-3 text-sm">
      <span className={`shrink-0 w-5 h-5 rounded-full text-[10px] font-semibold flex items-center justify-center mt-0.5 ${ACCENT_STYLES[accent] || ACCENT_STYLES.primary}`}>
        {index + 1}
      </span>
      <span className="text-foreground/80 leading-relaxed">{text}</span>
    </li>
  );
}

function SectionHeader({ icon: Icon, label, count, href, color }: {
  icon: any; label: string; count: number; href: string; color: string;
}) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2.5">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">{label}</h3>
        <Badge variant="secondary" className="text-[10px] rounded-full px-2 h-5 font-mono">{count}</Badge>
      </div>
      <Link href={href}>
        <span className="text-[11px] text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors cursor-pointer" data-testid={`link-view-all-${label.toLowerCase()}`}>
          View all <ChevronRight className="h-3 w-3" />
        </span>
      </Link>
    </div>
  );
}

function PersonaMiniCard({ persona, brandId }: { persona: Persona; brandId: string }) {
  const initials = persona.name.split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <Link href={`/brands/${brandId}/personas/${persona.id}`}>
      <div className="group flex items-center gap-3 p-3 rounded-xl border border-border/40 hover:border-border/80 hover:bg-muted/20 transition-all cursor-pointer" data-testid={`mini-persona-${persona.id}`}>
        <div className="relative flex-shrink-0">
          <div className="w-9 h-9 rounded-full overflow-hidden bg-muted/60 flex items-center justify-center border border-border/50">
            {persona.imageUrl ? (
              <img src={optimizedImg(persona.imageUrl, 72)} alt={persona.name} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <span className="text-[10px] font-semibold text-muted-foreground">{initials}</span>
            )}
          </div>
          {persona.status === "active" && (
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{persona.name}</span>
          </div>
          <p className="text-[12px] text-muted-foreground line-clamp-1 mt-0.5">{persona.summary || persona.primaryPain || "No summary"}</p>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/20 group-hover:text-muted-foreground/50 transition-colors shrink-0" />
      </div>
    </Link>
  );
}

function ProductMiniCard({ product, brandId, reviewStats }: {
  product: Product; brandId: string;
  reviewStats?: { count: number; avgRating: number | null };
}) {
  const media = (product.mediaJson as string[] | null) || [];
  const thumb = media[0] || null;

  return (
    <Link href={`/brands/${brandId}/products/${product.id}`}>
      <div className="group flex items-center gap-3 p-3 rounded-xl border border-border/40 hover:border-blue-500/20 hover:bg-blue-500/[0.02] transition-all cursor-pointer" data-testid={`mini-product-${product.id}`}>
        <div className="w-12 h-12 rounded-lg bg-blue-500/5 border border-border/30 flex items-center justify-center shrink-0 overflow-hidden">
          {thumb ? (
            <img src={thumb} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <Package className="h-5 w-5 text-blue-400/40" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{product.name}</span>
            {product.category && (
              <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider shrink-0">{product.category}</span>
            )}
          </div>
          <p className="text-[12px] text-muted-foreground line-clamp-1 mt-0.5">{product.description || product.summary || product.jobToBeDone || "No description"}</p>
          {reviewStats && reviewStats.avgRating !== null && (
            <div className="flex items-center gap-1 mt-1">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star
                    key={i}
                    className={`h-2.5 w-2.5 ${i <= Math.round(reviewStats.avgRating!) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20"}`}
                  />
                ))}
              </div>
              <span className="text-[10px] text-muted-foreground/50 font-mono">{reviewStats.avgRating.toFixed(1)}</span>
              <span className="text-[10px] text-muted-foreground/30">({reviewStats.count})</span>
            </div>
          )}
        </div>
        {product.priceText && (
          <span className="text-[12px] font-semibold text-foreground/70 shrink-0">{product.priceText}</span>
        )}
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/20 group-hover:text-blue-500/50 transition-colors shrink-0" />
      </div>
    </Link>
  );
}

function OfferingMiniCard({ offering, brandId }: { offering: Offer; brandId: string }) {
  const description = offering.shortDescription || offering.summary || null;
  return (
    <Link href={`/offers/${offering.id}`}>
      <div className="group flex items-center gap-3 p-3 rounded-xl border border-border/40 hover:border-amber-500/20 hover:bg-amber-500/[0.02] transition-all cursor-pointer" data-testid={`mini-offering-${offering.id}`}>
        <div className="w-12 h-12 rounded-lg bg-amber-500/5 border border-border/30 flex items-center justify-center shrink-0">
          <Tag className="h-5 w-5 text-amber-400/60" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{offering.name}</span>
            {offering.offerCategory && (
              <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider shrink-0">{offering.offerCategory}</span>
            )}
          </div>
          {description && (
            <p className="text-[12px] text-muted-foreground line-clamp-1 mt-0.5">{description}</p>
          )}
        </div>
        {offering.offerType && (
          <span className="text-[10px] font-medium text-muted-foreground/60 shrink-0 uppercase tracking-wider">{offering.offerType}</span>
        )}
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/20 group-hover:text-amber-500/50 transition-colors shrink-0" />
      </div>
    </Link>
  );
}

function CampaignMiniCard({ campaign, brandId, personas, products, narratives }: {
  campaign: Campaign; brandId: string;
  personas?: Persona[]; products?: Product[]; narratives?: Narrative[];
}) {
  const persona = personas?.find(p => p.id === campaign.personaId);
  const product = products?.find(p => p.id === campaign.productId);
  const narrative = narratives?.find(n => n.id === campaign.narrativeId);
  const isActive = campaign.status === "active";

  return (
    <Link href={`/brands/${brandId}/campaigns/${campaign.id}`}>
      <div className="group flex items-center gap-3 p-3 rounded-xl border border-border/40 hover:border-emerald-500/20 hover:bg-emerald-500/[0.02] transition-all cursor-pointer" data-testid={`mini-campaign-${campaign.id}`}>
        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
          <Layers className="h-4 w-4 text-emerald-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{campaign.name}</span>
            <Badge className={`text-[9px] rounded-full px-1.5 h-4 border ${isActive ? "bg-green-500/8 text-green-600 border-green-500/15" : "bg-muted text-muted-foreground border-border/50"}`}>
              {campaign.status}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-muted-foreground/60">
            {persona && <span className="truncate max-w-[100px]">{persona.name}</span>}
            {persona && product && <span className="text-muted-foreground/30">×</span>}
            {product && <span className="truncate max-w-[100px]">{product.name}</span>}
            {(persona || product) && narrative && <span className="text-muted-foreground/30">×</span>}
            {narrative && <span className="truncate max-w-[100px]">{narrative.name}</span>}
          </div>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/20 group-hover:text-emerald-500/50 transition-colors shrink-0" />
      </div>
    </Link>
  );
}

function NarrativeMiniCard({ narrative, brandId }: { narrative: Narrative; brandId: string }) {
  return (
    <Link href={`/brands/${brandId}/narratives/${narrative.id}`}>
      <div className="group flex items-center gap-3 p-3 rounded-xl border border-border/40 hover:border-teal-500/20 hover:bg-teal-500/[0.02] transition-all cursor-pointer" data-testid={`mini-narrative-${narrative.id}`}>
        <div className="w-9 h-9 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0">
          <BookOpen className="h-4 w-4 text-teal-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{narrative.name}</span>
          </div>
          <p className="text-[12px] text-muted-foreground line-clamp-1 mt-0.5">{narrative.description || narrative.promise || "No description"}</p>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/20 group-hover:text-teal-500/50 transition-colors shrink-0" />
      </div>
    </Link>
  );
}

function AngleMiniCard({ angle, brandId }: { angle: NarrativeAngle; brandId: string }) {
  const familyColor = FAMILY_COLORS[angle.family ?? ""] ?? "bg-muted text-muted-foreground";
  const avgScore = Math.round(
    ((angle.resonanceScore ?? 0) + (angle.proofScore ?? 0) + (angle.distinctivenessScore ?? 0) + (angle.emotionalScore ?? 0)) / 4
  );

  return (
    <Link href={`/brands/${brandId}/narrative-angles/${angle.id}`}>
      <div className="group flex items-center gap-3 p-3 rounded-xl border border-border/40 hover:border-violet-500/20 hover:bg-violet-500/[0.02] transition-all cursor-pointer" data-testid={`mini-angle-${angle.id}`}>
        <div className="w-9 h-9 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4 text-violet-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{angle.name}</span>
            {angle.family && (
              <Badge className={`text-[9px] rounded-full px-1.5 h-4 border-0 ${familyColor}`}>
                {angle.family}
              </Badge>
            )}
            {angle.isFavorite && (
              <Zap className="h-3 w-3 text-amber-500 shrink-0" />
            )}
          </div>
          <p className="text-[12px] text-muted-foreground line-clamp-1 mt-0.5">{angle.summary || angle.coreTension || "No summary"}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] font-mono text-muted-foreground/50">{avgScore}/10</span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/20 group-hover:text-violet-500/50 transition-colors" />
        </div>
      </div>
    </Link>
  );
}

function EmptySection({ icon: Icon, label }: { icon: any; label: string }) {
  return (
    <div className="flex items-center justify-center py-6 text-center">
      <Icon className="h-5 w-5 text-muted-foreground/20 mr-2" />
      <p className="text-sm text-muted-foreground/40">No {label.toLowerCase()} yet</p>
    </div>
  );
}

export default function BrandDetailPage() {
  const params = useParams<{ brandId: string }>();
  const brandId = params.brandId;
  const { user } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const { data: brand, isLoading } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/brands/${brandId}`);
    },
    onSuccess: () => {
      toast({ title: "Brand archived", description: `${brand?.name ?? "Brand"} is no longer available, but its data has been retained.` });
      queryClient.invalidateQueries({ queryKey: ["/api/brands"] });
      setDeleteOpen(false);
      setDeleteConfirm("");
      navigate("/brands");
    },
    onError: (e: any) => {
      toast({ title: "Archive failed", description: e?.message || "Could not archive the brand.", variant: "destructive" });
    },
  });
  const { data: personas } = useQuery<Persona[]>({ queryKey: [`/api/brands/${brandId}/personas`], enabled: !!brandId });
  const { data: products } = useQuery<Product[]>({ queryKey: [`/api/brands/${brandId}/products`], enabled: !!brandId });
  const { data: adUnits } = useQuery<AdUnitType[]>({ queryKey: [`/api/brands/${brandId}/ad-units`], enabled: !!brandId });
  const { data: offers } = useQuery<Offer[]>({ queryKey: [`/api/brands/${brandId}/offers`], enabled: !!brandId });
  const { data: nodes } = useQuery<StrategyNode[]>({ queryKey: [`/api/brands/${brandId}/strategy-nodes`], enabled: !!brandId });
  const { data: campaigns } = useQuery<Campaign[]>({ queryKey: [`/api/brands/${brandId}/campaigns`], enabled: !!brandId });
  const { data: narratives } = useQuery<Narrative[]>({ queryKey: [`/api/brands/${brandId}/narratives`], enabled: !!brandId });
  const { data: angles } = useQuery<NarrativeAngle[]>({ queryKey: [`/api/brands/${brandId}/narrative-angles`], enabled: !!brandId });
  const { data: reviewStatsRaw } = useQuery<Record<string, { count: number; avgRating: number | null }>>({
    queryKey: [`/api/brands/${brandId}/product-review-stats`],
    enabled: !!brandId,
  });

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-32 rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!brand) return <div className="p-8">Brand not found</div>;

  const displayColors = brandDisplayColors(brand.colorPalette);
  const nonNegotiableTruths = brand.nonNegotiableTruths as string[] | null;
  const claimGuardrails = brand.claimGuardrails as string[] | null;
  const strategicPriorities = brand.strategicPriorities as string[] | null;
  const approvedPhrases = brand.approvedPhrases as string[] | null;
  const bannedPhrases = brand.bannedPhrases as string[] | null;
  const activeCampaigns = campaigns?.filter(c => c.status === "active").length || 0;
  const draftCampaigns = campaigns?.filter(c => c.status === "draft").length || 0;

  const favoriteAngles = angles?.filter(a => a.isFavorite) ?? [];
  const topAngles = favoriteAngles.length > 0 ? favoriteAngles.slice(0, 5) : (angles ?? []).slice(0, 5);

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-8">
      <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-card via-card to-primary/[0.03] px-8 py-8 fade-in-up">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <ImgWithFallback
              src={brand.logoUrl ? optimizedImg(brand.logoUrl, 160, 80) : undefined}
              alt={`${brand.name} logo`}
              className="w-full h-full object-contain"
              imgWrapperClassName="w-12 h-12 rounded-xl border border-border overflow-hidden bg-muted shrink-0 mt-0.5"
              data-testid="img-brand-logo"
              fallback={
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
              }
            />
            <div>
              <div className="flex items-center gap-3 mb-1.5">
                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-brand-name">{brand.name}</h1>
                <Badge variant={brand.status === "active" ? "default" : "secondary"} className="text-[10px] rounded-full px-2.5">{brand.status}</Badge>
              </div>
              {brand.category && (
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-2">{brand.category}</p>
              )}
              {brand.summary && (
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xl">{brand.summary}</p>
              )}
              {displayColors.length > 0 && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {displayColors.map((color, i) => (
                    <div key={i} className="group relative" data-testid={`header-color-swatch-${i}`}>
                      <div
                        className="w-7 h-7 rounded-lg border border-border/40 shadow-sm cursor-default transition-transform group-hover:scale-110"
                        style={{ backgroundColor: color.hex }}
                        title={`${color.label} (${color.hex})`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ShareDialog
              resourceType="brand"
              resourceId={brand.id}
              resourceName={brand.name}
              workspaceId={brand.workspaceId}
            />
            <Link href={`/brands/${brandId}/settings`}>
              <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="button-brand-settings">
                <Pencil className="h-3.5 w-3.5" />
                Edit Brand
              </Button>
            </Link>
            {user?.isMaster && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={() => setDeleteOpen(true)}
                data-testid="button-delete-brand"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Archive
              </Button>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirm(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Archive this brand?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  This will remove <span className="font-semibold text-foreground">{brand?.name}</span> from normal use.
                  Its data will be retained, including:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                  <li>All personas, products, and reviews</li>
                  <li>All campaigns, narratives, and angles</li>
                  <li>All briefs, AI jobs, and generated creative</li>
                  <li>All ad units, offers, and learnings</li>
                  <li>All UGC campaigns, scripts, and renders</li>
                  <li>All shares granted on this brand</li>
                </ul>
                <p className="font-medium text-foreground">Archived brands can be restored later by a future archive-management workflow.</p>
                <div className="space-y-1.5 pt-2">
                  <label className="text-xs text-foreground">
                    Type <span className="font-mono font-semibold">{brand?.name}</span> to confirm:
                  </label>
                  <Input
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder={brand?.name}
                    autoFocus
                    data-testid="input-delete-confirm"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteConfirm.trim() !== (brand?.name ?? "") || deleteMutation.isPending}
              onClick={(e) => { e.preventDefault(); deleteMutation.mutate(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Archiving…</>
              ) : (
                <>Archive brand</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 fade-in-up-delay-1">
        <NavStatCard title="Personas" value={personas?.length || 0} icon={Users} href={`/brands/${brandId}/personas`} color="bg-violet-500/10 text-violet-500" />
        <NavStatCard title="Products" value={products?.length || 0} icon={Package} href={`/brands/${brandId}/products`} color="bg-blue-500/10 text-blue-500" />
        <NavStatCard title="Narratives" value={narratives?.length || 0} icon={BookOpen} href={`/brands/${brandId}/narratives`} color="bg-teal-500/10 text-teal-500" />
        <NavStatCard title="Campaigns" value={campaigns?.length || 0} icon={Layers} href={`/brands/${brandId}/campaigns`} color="bg-emerald-500/10 text-emerald-500" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 fade-in-up-delay-1">
        <NavStatCard title="Ad Units" value={adUnits?.length || 0} icon={Megaphone} href={`/brands/${brandId}/ad-units`} color="bg-cyan-500/10 text-cyan-500" />
        <NavStatCard title="Offerings" value={offers?.length || 0} icon={Tag} href={`/brands/${brandId}/offers`} color="bg-amber-500/10 text-amber-500" />
        <NavStatCard title="Strategy Nodes" value={nodes?.length || 0} icon={Target} href={`/brands/${brandId}/matrix`} color="bg-rose-500/10 text-rose-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 fade-in-up-delay-2">
        <Card className="border-border/40">
          <CardContent className="p-5">
            <SectionHeader icon={Users} label="Personas" count={personas?.length || 0} href={`/brands/${brandId}/personas`} color="bg-violet-500/10 text-violet-500" />
            {personas && personas.length > 0 ? (
              <div className="space-y-2">
                {personas.slice(0, 5).map(p => (
                  <PersonaMiniCard key={p.id} persona={p} brandId={brandId!} />
                ))}
                {personas.length > 5 && (
                  <Link href={`/brands/${brandId}/personas`}>
                    <p className="text-[11px] text-center text-muted-foreground/50 hover:text-primary/70 cursor-pointer pt-1 transition-colors">
                      +{personas.length - 5} more
                    </p>
                  </Link>
                )}
              </div>
            ) : (
              <EmptySection icon={Users} label="Personas" />
            )}
          </CardContent>
        </Card>

        <Card className="border-border/40">
          <CardContent className="p-5">
            <SectionHeader icon={Package} label="Products" count={products?.length || 0} href={`/brands/${brandId}/products`} color="bg-blue-500/10 text-blue-500" />
            {products && products.length > 0 ? (
              <div className="space-y-2">
                {products.slice(0, 5).map(p => (
                  <ProductMiniCard key={p.id} product={p} brandId={brandId!} reviewStats={reviewStatsRaw?.[p.id]} />
                ))}
                {products.length > 5 && (
                  <Link href={`/brands/${brandId}/products`}>
                    <p className="text-[11px] text-center text-muted-foreground/50 hover:text-primary/70 cursor-pointer pt-1 transition-colors">
                      +{products.length - 5} more
                    </p>
                  </Link>
                )}
              </div>
            ) : (
              <EmptySection icon={Package} label="Products" />
            )}
          </CardContent>
        </Card>

        <Card className="border-border/40">
          <CardContent className="p-5">
            <SectionHeader icon={Tag} label="Offerings" count={offers?.length || 0} href={`/brands/${brandId}/offers`} color="bg-amber-500/10 text-amber-500" />
            {offers && offers.length > 0 ? (
              <div className="space-y-2">
                {offers.slice(0, 5).map(o => (
                  <OfferingMiniCard key={o.id} offering={o} brandId={brandId!} />
                ))}
                {offers.length > 5 && (
                  <Link href={`/brands/${brandId}/offers`}>
                    <p className="text-[11px] text-center text-muted-foreground/50 hover:text-primary/70 cursor-pointer pt-1 transition-colors">
                      +{offers.length - 5} more
                    </p>
                  </Link>
                )}
              </div>
            ) : (
              <EmptySection icon={Tag} label="Offerings" />
            )}
          </CardContent>
        </Card>

        <Card className="border-border/40">
          <CardContent className="p-5">
            <SectionHeader icon={Layers} label="Campaigns" count={campaigns?.length || 0} href={`/brands/${brandId}/campaigns`} color="bg-emerald-500/10 text-emerald-500" />
            {campaigns && campaigns.length > 0 ? (
              <div className="space-y-2">
                {campaigns.slice(0, 5).map(c => (
                  <CampaignMiniCard key={c.id} campaign={c} brandId={brandId!} personas={personas} products={products} narratives={narratives} />
                ))}
                {campaigns.length > 5 && (
                  <Link href={`/brands/${brandId}/campaigns`}>
                    <p className="text-[11px] text-center text-muted-foreground/50 hover:text-primary/70 cursor-pointer pt-1 transition-colors">
                      +{campaigns.length - 5} more
                    </p>
                  </Link>
                )}
              </div>
            ) : (
              <EmptySection icon={Layers} label="Campaigns" />
            )}
          </CardContent>
        </Card>

        <Card className="border-border/40">
          <CardContent className="p-5">
            <SectionHeader icon={BookOpen} label="Narratives" count={narratives?.length || 0} href={`/brands/${brandId}/narratives`} color="bg-teal-500/10 text-teal-500" />
            {narratives && narratives.length > 0 ? (
              <div className="space-y-2">
                {narratives.slice(0, 5).map(n => (
                  <NarrativeMiniCard key={n.id} narrative={n} brandId={brandId!} />
                ))}
                {narratives.length > 5 && (
                  <Link href={`/brands/${brandId}/narratives`}>
                    <p className="text-[11px] text-center text-muted-foreground/50 hover:text-primary/70 cursor-pointer pt-1 transition-colors">
                      +{narratives.length - 5} more
                    </p>
                  </Link>
                )}
              </div>
            ) : (
              <EmptySection icon={BookOpen} label="Narratives" />
            )}
          </CardContent>
        </Card>
      </div>

      {topAngles.length > 0 && (
        <Card className="border-border/40 fade-in-up-delay-3">
          <CardContent className="p-5">
            <SectionHeader
              icon={Sparkles}
              label={favoriteAngles.length > 0 ? "Top Angles" : "Narrative Angles"}
              count={angles?.length || 0}
              href={`/brands/${brandId}/narratives`}
              color="bg-violet-500/10 text-violet-500"
            />
            <div className="space-y-2">
              {topAngles.map(a => (
                <AngleMiniCard key={a.id} angle={a} brandId={brandId!} />
              ))}
              {(angles?.length || 0) > 5 && (
                <Link href={`/brands/${brandId}/narrative-angles`}>
                  <p className="text-[11px] text-center text-muted-foreground/50 hover:text-primary/70 cursor-pointer pt-1 transition-colors">
                    +{(angles?.length || 0) - topAngles.length} more
                  </p>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="doctrine" className="space-y-6 fade-in-up-delay-3">
        <TabsList className="bg-muted/40 rounded-full p-1 border border-border/30" data-testid="tabs-brand-detail">
          <TabsTrigger value="doctrine" className="rounded-full text-xs data-[state=active]:shadow-sm">Doctrine</TabsTrigger>
          <TabsTrigger value="truths" className="rounded-full text-xs data-[state=active]:shadow-sm">Non-Negotiables</TabsTrigger>
          <TabsTrigger value="strategy" className="rounded-full text-xs data-[state=active]:shadow-sm">Strategy</TabsTrigger>
          <TabsTrigger value="voice" className="rounded-full text-xs data-[state=active]:shadow-sm">Voice & Tone</TabsTrigger>
          <TabsTrigger value="design-system" className="rounded-full text-xs data-[state=active]:shadow-sm" data-testid="tab-design-system">Design System</TabsTrigger>
        </TabsList>

        <TabsContent value="doctrine">
          <Card className="border-border/40">
            <CardContent className="p-6 md:p-8 space-y-5">
              {brand.positioning && (
                <DoctrineBlock icon={Compass} label="Positioning" content={brand.positioning} accent="primary" variant="hero" />
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <DoctrineBlock icon={Target} label="Worldview" content={brand.worldview} accent="sky" />
                <DoctrineBlock icon={Swords} label="Enemy" content={brand.enemy} accent="rose" />
                <DoctrineBlock icon={Heart} label="Promise" content={brand.promise} accent="emerald" />
              </div>

              {brand.mission && (
                <DoctrineBlock icon={Flag} label="Mission" content={brand.mission} accent="amber" variant="hero" />
              )}

              {brand.founderStory && (
                <div className="relative rounded-2xl bg-muted/30 ring-1 ring-border/40 p-6">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-7 h-7 rounded-lg bg-background/70 flex items-center justify-center shadow-sm ring-1 ring-border/40">
                      <BookOpen className="h-3.5 w-3.5 text-foreground/70" />
                    </div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground/60">Founder Story</p>
                  </div>
                  <p className="text-[14px] text-foreground/85 leading-relaxed whitespace-pre-line">{brand.founderStory}</p>
                </div>
              )}

              {displayColors.length > 0 && (
                <div className="relative rounded-2xl bg-muted/30 ring-1 ring-border/40 p-6">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-background/70 flex items-center justify-center shadow-sm ring-1 ring-border/40">
                      <Palette className="h-3.5 w-3.5 text-foreground/70" />
                    </div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground/60">Color Palette</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {displayColors.map((color, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-background/60 ring-1 ring-border/30" data-testid={`color-palette-entry-${i}`}>
                        <div
                          className="w-10 h-10 rounded-lg border border-border/40 shrink-0 shadow-sm"
                          style={{ backgroundColor: color.hex }}
                          data-testid={`color-swatch-${i}`}
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground/85 truncate" data-testid={`color-label-${i}`}>{color.label}</p>
                          <p className="text-[11px] text-muted-foreground font-mono" data-testid={`color-hex-${i}`}>{color.hex}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="truths">
          <div className="space-y-5">
            <Card className="border-border/40">
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                    <Shield className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">Non-Negotiable Truths</h3>
                </div>
                {nonNegotiableTruths && nonNegotiableTruths.length > 0 ? (
                  <ol className="space-y-3">
                    {nonNegotiableTruths.map((truth, i) => (
                      <NumberedItem key={i} index={i} text={truth} />
                    ))}
                  </ol>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Shield className="h-8 w-8 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">No non-negotiable truths defined yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {claimGuardrails && claimGuardrails.length > 0 && (
              <Card className="border-border/40">
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-6 h-6 rounded-md bg-destructive/10 flex items-center justify-center">
                      <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                    </div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">Claim Guardrails</h3>
                  </div>
                  <ol className="space-y-3">
                    {claimGuardrails.map((g, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <span className="shrink-0 w-5 h-5 rounded-full bg-destructive/10 text-destructive text-[10px] font-semibold flex items-center justify-center mt-0.5">
                          {i + 1}
                        </span>
                        <span className="text-foreground/80 leading-relaxed">{g}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="strategy">
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Active Campaigns", value: activeCampaigns, icon: TrendingUp, color: "text-emerald-500 bg-emerald-500/10" },
                { label: "Draft Campaigns", value: draftCampaigns, icon: Layers, color: "text-amber-500 bg-amber-500/10" },
                { label: "Narratives", value: narratives?.length || 0, icon: BookOpen, color: "text-teal-500 bg-teal-500/10" },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-3 px-4 py-4 rounded-xl border border-border/40 bg-card">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.color}`}>
                    <s.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold tracking-tight leading-none">{s.value}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mt-0.5">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {strategicPriorities && strategicPriorities.length > 0 && (
              <Card className="border-border/40">
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                      <Flag className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">Strategic Priorities</h3>
                  </div>
                  <ol className="space-y-3">
                    {strategicPriorities.map((p, i) => (
                      <NumberedItem key={i} index={i} text={p} />
                    ))}
                  </ol>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="voice">
          <div className="space-y-5">
            {brand.toneSummary && (
              <Card className="border-border/40">
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                      <Volume2 className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">Tone Summary</h3>
                  </div>
                  <p className="text-sm text-foreground/85 leading-relaxed pl-8">{brand.toneSummary}</p>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Card className="border-border/40">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/10 flex items-center justify-center">
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                    </div>
                    <h3 className="text-[10px] font-semibold uppercase tracking-wider text-foreground/70">Approved Phrases</h3>
                  </div>
                  {approvedPhrases && approvedPhrases.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {approvedPhrases.map((p, i) => (
                        <Badge key={i} variant="secondary" className="text-xs rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-0 px-3 py-1">{p}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground/50 italic">None defined</p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/40">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-5 h-5 rounded-md bg-red-500/10 flex items-center justify-center">
                      <XCircle className="h-3 w-3 text-red-500" />
                    </div>
                    <h3 className="text-[10px] font-semibold uppercase tracking-wider text-foreground/70">Banned Phrases</h3>
                  </div>
                  {bannedPhrases && bannedPhrases.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {bannedPhrases.map((p, i) => (
                        <Badge key={i} variant="secondary" className="text-xs rounded-full bg-red-500/10 text-red-700 dark:text-red-400 border-0 px-3 py-1">{p}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground/50 italic">None defined</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="design-system">
          <BrandDesignSystemTab brand={brand} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
